// /case-studies — Public-facing proof in practice. All client names anonymized.
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicNav from '@/components/PublicNav';
import PublicFooter from '@/components/PublicFooter';
import { useSEO } from '@/hooks/useSEO';
import { usePageTracker } from '@/hooks/usePageTracker';
import {
  ArrowRight, Building2, Globe2, FlaskConical, CheckCircle2, Layers, Sparkles, MapPin
} from 'lucide-react';

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } }, { threshold });
    obs.observe(el); return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const CASES = [
  {
    id: 'eu-specialty',
    audience: 'For Pharma BD',
    audienceColor: '#00709B',
    audienceIcon: <Building2 className="w-3.5 h-3.5" />,
    client: 'Confidential · European Specialty Pharma',
    location: 'European Union · 25+ markets',
    headline: 'EU commercialization-ready in-licensing shortlist',
    subhead: 'Deck-aligned deterministic scoring + live web enrichment',
    problem: 'A European specialty pharma operating across 25+ EU markets needed a continuously refreshed shortlist of in-licensing-ready assets that fit their EU commercialization muscle. They did not need another sprawling database; they needed a deck-aligned, deterministically scored list with the payer story already pre-flagged.',
    approach: [
      'Encoded every selection rule from the client\'s corporate-development deck (therapeutic-area weighting, stage gates, EU-rights filters, fit-tier sub-buckets) into a deterministic scoring engine — same yardstick on every asset.',
      'Layered live web enrichment from Perplexity Sonar across every shortlisted asset to surface the latest stage, indication, MOA, and any post-deck news with full source citations.',
      'Pre-flagged EU JCA readiness on every in-scope oncology / ATMP asset, so the BD team could see the payer-path risk before opening diligence.',
      'Delivered as both a branded Excel export (monthly refresh) and a live workspace inside the ForesightRx platform for the client\'s BD team.',
    ],
    deliverables: [
      'Deterministic scoring engine encoding 12 selection rules',
      'Per-asset Perplexity enrichment with cited sources',
      'Sub-bucket fit tiering (Tier 1 / Tier 2 / Watchlist)',
      'EU-rights status & regulatory-readiness flags',
      'Monthly branded Excel export',
      'Live workspace for the BD team',
    ],
    outcome: 'BD now opens diligence only on assets that have already cleared the internal deck criteria and have a credible payer pathway. Wasted-meeting rate dropped materially within the first quarter of the engagement.',
  },
  {
    id: 'cross-border',
    audience: 'For Pharma BD · Cross-border',
    audienceColor: '#70C24C',
    audienceIcon: <Globe2 className="w-3.5 h-3.5" />,
    client: 'Confidential · US Specialty Pharma',
    location: 'China → Western markets',
    headline: 'China-origin pipeline into Western pharma',
    subhead: 'Living shortlist sourced from NMPA, ChinaBio floor, and WeChat signal channels',
    problem: 'A US-based specialty pharma needed a continuous flow of China-origin differentiated assets — oncology, immunology, rare disease — that were available for Western rights and could survive payer scrutiny in EU and US markets. Off-the-shelf databases showed Chinese pipelines weeks (sometimes quarters) after the window closed.',
    approach: [
      'Built a living China-origin shortlist sourced from NMPA filings, ChinaBio Partnering Forum floor coverage, CPIC Shanghai, WeChat / vernacular signal channels, and direct BD relationships in Shanghai, Beijing, and Suzhou.',
      'Pre-screened every asset for EU rights availability, JCA readiness signal for in-scope indications, and US AMCP / payer-value plausibility — so the BD team only evaluates assets with a credible Western commercialization path.',
      'On-site BD at four China-focused conferences per year with structured one-on-one meeting facilitation and same-day flash reports.',
      'Cross-border BD network in Shanghai, Beijing, Suzhou, and Guangzhou — local-language fluency, not translated databases.',
    ],
    deliverables: [
      'Continuous China-origin asset shortlist, refreshed weekly',
      'EU-rights availability flag on every asset',
      'JCA readiness signal for in-scope EU indications',
      'US AMCP / payer-value plausibility pre-read',
      'WeChat & vernacular channel signal monitoring',
      'On-site BD at ChinaBio and CPIC Shanghai',
    ],
    outcome: 'The client now sees China-origin assets weeks before they surface in English-language databases — and only evaluates those with a credible Western payer pathway already pre-flagged.',
  },
  {
    id: 'biotech-anon',
    audience: 'For Emerging Biotech',
    audienceColor: '#A88BFF',
    audienceIcon: <FlaskConical className="w-3.5 h-3.5" />,
    client: 'Confidential — Oncology Biotech',
    location: 'Series A · Phase 1b oncology',
    headline: 'From "great science, no deal" to first BD term sheet',
    subhead: 'Out-licensing positioning + EU JCA pre-read + curated intros — success-only fee',
    problem: 'A Series-A oncology biotech with a differentiated Phase 1b asset had been pitching pharma BD for nine months without traction. Pharma reviewers liked the science but kept asking the same two questions the company could not answer cleanly: where is the comparator story for EU JCA, and what does the US AMCP package look like at launch?',
    approach: [
      'Reframed the asset story into the Problem → Patient → Proof framework — the language pharma BD reviewers respond to.',
      'Built a structured EU JCA pre-read: PICO mapping, comparator landscape, ITC feasibility assessment, and a clinical / payer evidence-gap analysis.',
      'Sketched the US AMCP-format dossier outline + budget-impact narrative + likely payer objections and response strategy.',
      'Curated a 24-name pharma BD target list, ran personalized outreach, and facilitated NCD exchange and CDA orchestration for the four most engaged counterparties.',
    ],
    deliverables: [
      'BD-ready non-confidential deck (Problem → Patient → Proof)',
      'EU JCA / HTA pre-read with PICO + comparator landscape',
      'US AMCP-format dossier outline + payer-objection map',
      '24-name curated BD target list with rationale',
      'Personalized outreach + CDA orchestration',
      'Success-only fee — paid on signed term sheet',
    ],
    outcome: 'From nine months of no traction to a first term sheet within six months of engagement. The biotech paid nothing until the deal moved — aligned incentives, demonstrated through outcome.',
  },
];

export default function CaseStudiesPage() {
  useSEO({
    title: 'Case Studies — Proof in Practice | ForesightRx',
    description: 'Real anonymized engagements: EU specialty pharma in-licensing, US cross-border China sourcing, and emerging-biotech out-licensing. Deck-aligned scoring, cross-border BD, JCA & AMCP readiness.',
    canonical: 'https://foresightrx.com/case-studies',
  });
  usePageTracker('case-studies');

  return (
    <div className="min-h-screen bg-[#0A1128] copy-protect" data-testid="case-studies-page">
      <PublicNav />

      {/* HERO */}
      <section className="relative pt-32 lg:pt-40 pb-16 lg:pb-20 overflow-hidden" data-testid="case-studies-hero">
        <div className="absolute inset-0 grid-bg" />
        <div className="absolute top-1/3 -right-32 w-[500px] h-[500px] rounded-full bg-[#00709B]/12 blur-[120px] pulse-glow" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="reveal-up max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#70C24C] mb-4">Case Studies</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-tighter leading-[1.05]" data-testid="case-studies-heading">
              Proof in practice.
            </h1>
            <p className="mt-7 text-lg lg:text-xl text-[#A1A8C3] font-light max-w-2xl leading-relaxed">
              We don't sell potential — we point at what already shipped. Three engagements that
              show the same operating model across Pharma BD, cross-border sourcing, and emerging-biotech
              out-licensing.
            </p>
          </div>
        </div>
      </section>

      {/* CASES */}
      <div>
        {CASES.map((c, i) => (
          <CaseSection key={c.id} c={c} alt={i % 2 === 1} />
        ))}
      </div>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-[#111A3A] relative overflow-hidden" data-testid="case-studies-cta">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-[#00709B]/10 to-[#70C24C]/10 blur-[100px]" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mb-6">
            Your engagement is the next case study.
          </h2>
          <p className="text-lg text-[#A1A8C3] font-light mb-10 max-w-xl mx-auto">
            Pharma BD looking for cross-border deal flow. Biotech with a partnerable asset and no path to the right table.
            Tell us which one you are.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/for-pharma" className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#70C24C] text-[#0A1128] text-sm font-bold hover:bg-[#5fad3b] hover:-translate-y-0.5 transition-all duration-300 shadow-[0_0_30px_rgba(112,194,76,0.25)]" data-testid="case-studies-cta-pharma">
              I'm Pharma BD <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/for-biotech" className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/15 text-white text-sm font-medium hover:bg-white/5 transition-all duration-300" data-testid="case-studies-cta-biotech">
              I'm an Emerging Biotech
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

function CaseSection({ c, alt }) {
  const [ref, inView] = useInView(0.1);
  return (
    <section
      ref={ref}
      id={c.id}
      className={`py-24 lg:py-32 ${alt ? 'bg-[#0E1733]' : 'bg-[#0A1128]'} scroll-mt-24`}
      data-testid={`case-section-${c.id}`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className={`grid lg:grid-cols-[1.5fr_1fr] gap-10 lg:gap-14 items-start ${inView ? 'reveal-up' : 'opacity-0'}`}>
          {/* LEFT — Narrative */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-5" style={{ backgroundColor: `${c.audienceColor}15`, color: c.audienceColor }}>
              {c.audienceIcon}
              <span>{c.audience}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-[1.1] mb-4">
              {c.headline}
            </h2>
            <p className="text-base text-[#70C24C] font-semibold mb-6">{c.subhead}</p>

            <div className="flex flex-wrap items-center gap-4 mb-8 text-xs text-[#A1A8C3]">
              <span className="font-semibold text-white">{c.client}</span>
              <span className="w-1 h-1 rounded-full bg-[#687399]" />
              <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.location}</span>
            </div>

            <div className="mb-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#70C24C] mb-2.5">The Problem</p>
              <p className="text-base text-[#A1A8C3] font-light leading-relaxed">{c.problem}</p>
            </div>

            <div className="mb-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#00709B] mb-3">Our Approach</p>
              <ul className="space-y-3">
                {c.approach.map((a, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-[#A1A8C3] font-light leading-relaxed">
                    <span className="text-[#70C24C] font-bold shrink-0 mt-0.5">{String(i + 1).padStart(2, '0')}</span>
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-5 rounded-2xl bg-gradient-to-br from-[#70C24C]/8 to-[#00709B]/8 border border-white/5">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#70C24C] mb-2">Outcome</p>
              <p className="text-sm text-[#A1A8C3] font-light leading-relaxed">{c.outcome}</p>
            </div>
          </div>

          {/* RIGHT — Deliverables sidebar */}
          <div className="lg:sticky lg:top-28">
            <div className="bg-[#111A3A] border border-white/5 rounded-2xl p-6 lg:p-7">
              <div className="flex items-center gap-2 mb-4">
                <Layers className="w-4 h-4 text-[#70C24C]" />
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#70C24C]">What was delivered</p>
              </div>
              <ul className="space-y-3">
                {c.deliverables.map((d, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-[#A1A8C3] font-light">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-[#70C24C]" />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
